import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAlert } from '../hooks/useAlert';
import { ThemeProvider } from '../contexts/ThemeContext';
import CustomAlert from '../components/CustomAlert';
import './SharedTranscript.css';

function SharedTranscript() {
  const { shareId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transcriptData, setTranscriptData] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [includesAudio, setIncludesAudio] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcriptInfo, setTranscriptInfo] = useState(null);

  const audioRef = useRef(null);
  const copyActionProcessedRef = useRef(false);

  const { isAuthenticated } = useAuth();
  const { alert, showAlert } = useAlert();

  // Force light theme for shared pages
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', '#f8fafc');
    }
  }, []);

  useEffect(() => {
    fetchSharedTranscript();
  }, [shareId]);

  // Auto-trigger copy if user just logged in with action=copy
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'copy' && isAuthenticated && transcriptInfo && !copyActionProcessedRef.current) {
      copyActionProcessedRef.current = true;
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
      setTimeout(() => handleCloneToMyAccount(), 100);
    }
  }, [isAuthenticated, transcriptInfo, searchParams]);

  const fetchSharedTranscript = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await fetch(`/api/shared/${shareId}`);
      const data = await response.json();

      if (response.ok) {
        setTranscriptData(data.transcript.currentVersionData);
        setTranscriptInfo(data.transcript);
        setIncludesAudio(data.shareInfo.includeAudio);

        if (data.transcript.audioHash && data.shareInfo.includeAudio) {
          const audioEndpoint = `/api/shared/${shareId}/audio`;
          try {
            const audioResponse = await fetch(audioEndpoint);
            if (audioResponse.ok) {
              const contentType = audioResponse.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const audioData = await audioResponse.json();
                setAudioUrl(audioData.url || audioData.signedUrl);
              } else {
                setAudioUrl(audioEndpoint);
              }
            }
          } catch (audioError) {
            console.error('[SharedTranscript] Error fetching audio:', audioError);
          }
        }
      } else {
        setError(data.error || 'Failed to load shared transcript');
      }
    } catch (err) {
      setError('Error loading shared transcript');
      console.error('Error fetching shared transcript:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloneToMyAccount = async () => {
    if (!transcriptInfo) {
      showAlert('Error', 'No transcript info available');
      return;
    }

    if (!isAuthenticated) {
      const returnUrl = encodeURIComponent(window.location.pathname);
      window.location.href = `/auth?redirect=${returnUrl}&action=copy`;
      return;
    }

    try {
      let duration = transcriptInfo.duration;
      if (!duration && transcriptData?.segments) {
        duration = transcriptData.segments.reduce((max, segment) =>
          Math.max(max, segment.end || 0), 0
        );
      }

      const response = await fetch('/api/transcripts', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `Copy of ${transcriptInfo.name || 'Shared Transcript'}`,
          data: transcriptData,
          originalFileName: transcriptInfo.originalFileName || 'transcript.mp3',
          duration: duration || 0,
          tags: transcriptInfo.tags || [],
          audioHash: transcriptInfo.audioHash || null,
          source: 'shared_copy'
        })
      });

      if (response.ok) {
        const newTranscript = await response.json();
        if (newTranscript?.id) {
          window.location.href = `/?transcript=${encodeURIComponent(newTranscript.id)}`;
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showAlert('Copy Failed', errorData.error || 'Failed to copy transcript');
      }
    } catch (err) {
      showAlert('Error', err.message || 'Unknown error occurred');
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get transcript text
  const getTranscriptText = () => {
    if (!transcriptData?.words) return '';
    return transcriptData.words
      .filter(w => !w.deleted)
      .map(w => w.word)
      .join(' ');
  };

  // Generate waveform bars
  const generateWaveformBars = (count = 50) => {
    const bars = [];
    for (let i = 0; i < count; i++) {
      const position = i / count;
      const baseHeight = Math.sin(position * Math.PI) * 0.6 + 0.2;
      const randomVariation = (Math.random() - 0.5) * 0.3;
      bars.push(Math.max(0.1, Math.min(1, baseHeight + randomVariation)));
    }
    return bars;
  };

  const [waveformBars] = useState(() => generateWaveformBars());
  const progressPercentage = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const handleWaveformClick = (e) => {
    if (!audioRef.current || !audioUrl) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percentage = (e.clientX - rect.left) / rect.width;
    const newTime = percentage * audioDuration;
    audioRef.current.currentTime = newTime;
    if (!isPlaying) audioRef.current.play();
  };

  if (isLoading) {
    return (
      <ThemeProvider>
        <div className="shared-transcript-page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading transcript...</p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider>
        <div className="shared-transcript-page">
          <div className="error-container">
            <div className="error-card">
              <h2>Unable to Load</h2>
              <p>{error}</p>
              <Link to="/" className="home-link">Go to Rec-Chord</Link>
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  const transcriptText = getTranscriptText();

  return (
    <ThemeProvider>
      <div className="shared-transcript-page card-view">
        {/* Hidden audio element */}
        {audioUrl && (
          <audio
            ref={audioRef}
            src={audioUrl}
            onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
            onLoadedMetadata={(e) => setAudioDuration(e.target.duration)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => { setIsPlaying(false); setCurrentTime(0); }}
            preload="metadata"
          />
        )}

        {/* Main Card */}
        <div className="shared-card">
          {/* Card Header */}
          <div className="shared-card-header">
            <Link to="/" className="shared-card-logo">
              <div className="logo-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                </svg>
              </div>
              <span>Rec-Chord</span>
            </Link>
            {audioDuration > 0 && (
              <div className="shared-card-duration">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{formatDuration(audioDuration)}</span>
              </div>
            )}
          </div>

          {/* Audio Player */}
          {audioUrl && (
            <div className="shared-card-player" onClick={handleWaveformClick}>
              <button
                className={`player-play-btn ${isPlaying ? 'playing' : ''}`}
                onClick={(e) => { e.stopPropagation(); togglePlayPause(); }}
              >
                {isPlaying ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0A.75.75 0 0115 4.5h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75V5.25z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                  </svg>
                )}
              </button>

              <div className="player-waveform">
                {waveformBars.map((height, index) => {
                  const barPosition = (index / waveformBars.length) * 100;
                  const isPlayed = barPosition < progressPercentage;
                  return (
                    <div
                      key={index}
                      className={`waveform-bar ${isPlayed ? 'played' : ''}`}
                      style={{ '--bar-height': `${height * 100}%` }}
                    />
                  );
                })}
              </div>

              <div className="player-time">
                {formatDuration(currentTime)}
              </div>
            </div>
          )}

          {/* Transcript Title */}
          {transcriptInfo?.name && (
            <h1 className="shared-card-title">{transcriptInfo.name}</h1>
          )}

          {/* Transcript Content */}
          <div className="shared-card-content">
            <p>{transcriptText}</p>
          </div>

          {/* Card Footer with CTA */}
          <div className="shared-card-footer">
            <Link to="/" className="try-recchord-btn">
              <span>Try</span>
              <strong>Rec-Chord</strong>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>

        <CustomAlert
          isOpen={alert?.isOpen || false}
          onClose={alert?.onClose}
          onConfirm={alert?.onConfirm}
          title={alert?.title}
          message={alert?.message}
          type={alert?.type}
          confirmText={alert?.confirmText}
        />
      </div>
    </ThemeProvider>
  );
}

export default SharedTranscript;
